import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, ArrowLeft, Trash2, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const CartScreen = () => {
  const { cartItems, removeFromCart, updateQty, subtotal, cartCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!user) {
      navigate('/login?redirect=checkout');
    } else {
      navigate('/checkout');
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="flex flex-col lg:flex-row gap-12">
        <div className="lg:w-2/3 space-y-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-secondary-900">Shopping Cart</h1>
            <span className="text-secondary-500 font-medium">{cartCount} items</span>
          </div>

          {cartItems.length === 0 ? (
            <div className="bg-primary-50 rounded-3xl p-12 text-center space-y-4">
              <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto shadow-sm">
                <ShoppingCart className="h-10 w-10 text-primary-400" />
              </div>
              <h2 className="text-2xl font-bold text-secondary-900">Your cart is empty</h2>
              <p className="text-secondary-600">Looks like you haven't added anything yet.</p>
              <Link to="/" className="inline-block bg-primary-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-primary-700 transition-all">
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item._id} className="bg-white rounded-3xl p-6 border border-secondary-100 flex items-center space-x-6 hover:shadow-md transition-shadow">
                  <Link to={`/product/${item._id}`}>
                    <img src={item.image} alt={item.name} className="w-24 h-24 rounded-2xl object-cover" />
                  </Link>
                  <div className="flex-1 space-y-1">
                    <Link to={`/product/${item._id}`}><h3 className="text-lg font-bold text-secondary-900 hover:text-primary-600 transition-colors">{item.name}</h3></Link>
                    <p className="text-sm text-secondary-500">₹{item.price} each</p>
                  </div>
                  <div className="flex items-center border border-secondary-200 rounded-xl p-1">
                    <button onClick={() => updateQty(item._id, item.qty - 1)} className="p-2 hover:bg-secondary-50 rounded-lg transition-colors font-bold w-10">-</button>
                    <span className="w-10 text-center font-bold">{item.qty}</span>
                    <button onClick={() => updateQty(item._id, item.qty + 1)} className="p-2 hover:bg-secondary-50 rounded-lg transition-colors font-bold w-10">+</button>
                  </div>
                  <div className="text-right w-24">
                    <p className="text-lg font-bold text-secondary-900">₹{item.price * item.qty}</p>
                  </div>
                  <button onClick={() => removeFromCart(item._id)} className="p-2 text-red-400 hover:text-red-600 transition-colors">
                    <Trash2 className="h-6 w-6" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Link to="/" className="inline-flex items-center space-x-2 text-primary-600 font-bold hover:translate-x-1 transition-transform">
            <ArrowLeft className="h-5 w-5" />
            <span>Continue Shopping</span>
          </Link>
        </div>

        {cartItems.length > 0 && (
          <div className="lg:w-1/3">
            <div className="bg-secondary-900 text-white rounded-[32px] p-8 sticky top-28 shadow-2xl overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary-600/20 blur-3xl rounded-full transform translate-x-1/2 -translate-y-1/2" />
              <h2 className="text-2xl font-bold mb-8">Order Summary</h2>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between text-secondary-300">
                  <span>Subtotal ({cartCount} items)</span>
                  <span>₹{subtotal}</span>
                </div>
                <div className="flex justify-between text-secondary-300">
                  <span>Shipping</span>
                  <span className="text-primary-400 font-bold">FREE</span>
                </div>
                <div className="pt-4 border-t border-secondary-800 flex justify-between">
                  <span className="text-xl font-bold">Total</span>
                  <span className="text-2xl font-bold text-primary-400">₹{subtotal}</span>
                </div>
              </div>
              <button onClick={handleCheckout} className="w-full bg-primary-500 hover:bg-primary-400 text-white py-4 rounded-2xl font-bold transition-all flex items-center justify-center space-x-3 group">
                <span>Proceed to Checkout</span>
                <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartScreen;
